
import { supabase } from '@/integrations/supabase/client';

export type ActivityType = 
  | 'login' 
  | 'data_export'
  | 'mfa_verification'
  | 'mfa_enrollment'
  | 'merchant_type_change'
  | 'merchant_created'
  | 'merchant_details_change'
  | 'admin_mode_enabled'
  | 'merchant_status_change'
  | 'merchant_contact_change'
  // PO activity types
  | 'po_created'
  | 'po_status_changed'
  | 'po_archived'
  | 'po_line_item_updated'
  | 'po_line_item_verified'

interface ActivityLogData {
  userId?: string;
  userEmail: string;
  activityType: ActivityType;
  activityDetails?: any;
  ipAddress?: string;
  userAgent?: string;
}

const createTimeoutPromise = (timeoutMs: number) => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Timeout')), timeoutMs);
  });
};

export const logUserActivity = async ({
  userId,
  userEmail,
  activityType,
  activityDetails,
  ipAddress,
  userAgent
}: ActivityLogData): Promise<boolean> => {
  try {

    const timeoutPromise = createTimeoutPromise(2000);
    // The log_user_activity RPC is no longer executable by signed-in users
    // (revoked in 20260728122000_audit_security_definer_functions_uc107.sql),
    // so write the row directly. RLS only accepts the caller's own user_id
    // (FIG-374). A trusted, server-side history is UC-215
    // (.foundry/spec/fig-316/use-cases).
    // No .select(): only admins may read the table back.
    const insert = (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return supabase.from('user_activity_logs').insert({
        user_id: session?.user?.id ?? userId ?? null,
        user_email: userEmail,
        activity_type: activityType,
        activity_details: activityDetails || null,
        ip_address: ipAddress || null,
        user_agent: userAgent || navigator.userAgent
      });
    })();

    type ResultType = { error?: unknown };

    const result = await Promise.race([insert, timeoutPromise]) as ResultType;
    const { error } = result;

    if (error) {
      console.error('[ActivityLogger] Error logging user activity:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('[ActivityLogger] Error logging user activity:', error);
    return false;
  }
};

// Helper function to get user's IP address (simplified version)
export const getUserIP = async (): Promise<string | null> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('[ActivityLogger] Error getting user IP:', error);
    return null;
  }
};

// Helper function to log login activities
export const logLoginActivity = async (
  userEmail: string, 
  method: 'password' | 'magic_link', 
  userId?: string,
  submissionIdentifier?: string
) => {
  try {
    const ipAddress = await getUserIP();
    
    const activityDetails = {
      method,
      timestamp: new Date().toISOString(),
      ...(submissionIdentifier && { submission_identifier: submissionIdentifier })
    };

    return await logUserActivity({
      userId,
      userEmail,
      activityType: 'login',
      activityDetails,
      ipAddress
    });
  } catch (error) {
    console.error('[ActivityLogger] Error logging login activity:', error);
    return false;
  }
};

// Helper function to log MFA verification events
export const logMfaVerification = async (userEmail: string, success: boolean, factorType: 'email' | 'sms' = 'email') => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'mfa_verification',
    activityDetails: {
      success,
      factorType,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log MFA enrollment events
export const logMfaEnrollment = async (userEmail: string, factorType: 'email' | 'sms' = 'email') => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'mfa_enrollment',
    activityDetails: {
      factorType,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log merchant type changes
export const logMerchantTypeChange = async (
  userEmail: string,
  organisationId: string,
  organisationName: string,
  oldType: string,
  newType: string
) => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'merchant_type_change',
    activityDetails: {
      organisation_id: organisationId,
      organisation_name: organisationName,
      old_value: oldType,
      new_value: newType,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log an admin turning on admin mode on a merchant's
// Settings tab (FIG-345 item 7): who, when and which merchant. Written from
// the browser like the other helpers here, so it is an audit trail, not a
// control; the server-side history writer arrives with FIG-345 PR 2.
export const logAdminModeEnabled = async (
  userEmail: string,
  organisationId: string,
  organisationName: string,
  userId?: string
) => {
  const ipAddress = await getUserIP();

  return logUserActivity({
    userId,
    userEmail,
    activityType: 'admin_mode_enabled',
    activityDetails: {
      organisation_id: organisationId,
      organisation_name: organisationName,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log a merchant added from Admin › Merchants (FIG-319)
export const logMerchantCreated = async (
  userEmail: string,
  organisationId: string,
  organisationName: string,
  options: { isTestClient: boolean; userId?: string }
) => {
  const ipAddress = await getUserIP();

  return logUserActivity({
    userId: options.userId,
    userEmail,
    activityType: 'merchant_created',
    activityDetails: {
      organisation_id: organisationId,
      organisation_name: organisationName,
      test_client: options.isTestClient,
      // Every new merchant starts as Onboarding, except an auto-filled test client (REQ-005, REQ-037).
      summary: options.isTestClient ? 'Created as Test (auto-filled test client)' : 'Created as Onboarding',
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log a Merchant Details edit on the Settings tab (FIG-320 / REQ-039)
export const logMerchantDetailsChange = async (
  userEmail: string,
  organisationId: string,
  organisationName: string,
  changedFields: string[],
  userId?: string
) => {
  const ipAddress = await getUserIP();

  return logUserActivity({
    userId,
    userEmail,
    activityType: 'merchant_details_change',
    activityDetails: {
      organisation_id: organisationId,
      organisation_name: organisationName,
      changed_fields: changedFields,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log PO status changes
export const logPOStatusChange = async (
  userEmail: string,
  purchaseOrderId: string,
  poNumber: string,
  oldStatus: string,
  newStatus: string
) => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'po_status_changed',
    activityDetails: {
      purchase_order_id: purchaseOrderId,
      po_number: poNumber,
      old_value: oldStatus,
      new_value: newStatus,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log PO archive actions
export const logPOArchive = async (
  userEmail: string,
  purchaseOrderId: string,
  poNumber: string,
  reason: string
) => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'po_archived',
    activityDetails: {
      purchase_order_id: purchaseOrderId,
      po_number: poNumber,
      reason,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log PO line item updates
export const logPOLineItemUpdate = async (
  userEmail: string,
  purchaseOrderId: string,
  lineItemId: string,
  changes: Record<string, { old: unknown; new: unknown }>
) => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'po_line_item_updated',
    activityDetails: {
      purchase_order_id: purchaseOrderId,
      line_item_id: lineItemId,
      changes,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log PO line item verification
export const logPOLineItemVerification = async (
  userEmail: string,
  purchaseOrderId: string,
  lineItemIds: string[],
  isBulk: boolean = false
) => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'po_line_item_verified',
    activityDetails: {
      purchase_order_id: purchaseOrderId,
      line_item_ids: lineItemIds,
      is_bulk: isBulk,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};
